import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type CapabilityEntry } from "../lib/api";
import { settingsFeed } from "../lib/settingsFeed";
import { NAMELESS_MODEL, connections, modelNames, type ConnectionSettings } from "./api";
import { Icon } from "../components/Icon";
import { ErrorState } from "../shell/ui";
import { connectionHref } from "./navigation";
import { ModelChoice } from "./ModelChoice";
import { ModelLevel } from "./ModelLevel";
import { modelSettlement, type DeclaredModelCheck } from "./modelSettlement";

/**
 * The configured default and the live registry answer different questions.
 *
 * Which two namespaces those answers live in is not this file's to know: the
 * setting that declares `readiness` is the one holding the model declarations,
 * and `readiness.default_namespace` / `default_field` is where the chosen
 * default is stored (`plugins/llm_providers/plugin.yaml`), as
 * `declaredModelCheck` reads them. The shell used to write these names here,
 * and a link back to one of them (D380). With `editable`, this is the one
 * place the default is chosen: the form of the namespace holding it leaves
 * the field out.
 *
 * What an *unset* default means is not this file's either. With nothing
 * chosen, a turn calls the model its plugin registered as standing in (for
 * `llm_providers` the first in its list, `service.py` `_default_key`), and this
 * page marks that one in use. It never decides for itself when an empty box is
 * a problem: it used to, and it told somebody who had connected their only
 * model, and later somebody who had added a backup to it, 「还未选择对话模型」
 * while the Agent answered. The decision is `modelSettlement`, which the Hub's
 * creation flow applies too.
 */

export function ModelReadiness({
  version = 0,
  sessionId,
  editable = false,
  onlyWhenNeeded = false,
  onToast,
  active = true,
  onStatus,
  compact = false,
  onModels,
  check,
}: {
  version?: number;
  sessionId?: string;
  editable?: boolean;
  onlyWhenNeeded?: boolean;
  onToast?: (message: string, tone: "good" | "critical") => void;
  active?: boolean;
  onStatus?: (status: "loading" | "ready" | "missing" | "unknown") => void;
  compact?: boolean;
  /** Told the models registered each time they are read, for a page that arranges itself around them. */
  onModels?: (models: CapabilityEntry[]) => void;
  /** The check, as the Agent's settings declare it (`declaredModelCheck`). */
  check: DeclaredModelCheck;
}) {
  const { namespace, defaultNamespace, defaultField, fallbackField } = check;
  const [state, setState] = useState<{
    settings: ConnectionSettings;
    registered: CapabilityEntry[];
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [refresh, setRefresh] = useState(0);
  const reload = useCallback(() => setRefresh(n => n + 1), []);
  // What this shows moves when one of its two settings is written, and the
  // stream says which (`settingsFeed`); registrations move with `version`, and
  // a connection coming back with `active`. Nothing else re-reads it.
  useEffect(() => settingsFeed.subscribe(({ namespace: written }) => {
    if (written === null || written === namespace || written === defaultNamespace) reload();
  }), [namespace, defaultNamespace, reload]);
  // The model being switched to, until the choice is read back: the list shows what the Agent holds, never
  // the row that was clicked before the Agent answered.
  const [switching, setSwitching] = useState<string | null>(null);
  const [choiceError, setChoiceError] = useState<unknown>(null);
  // The same for the level of the models somebody else supplies, when the plugin declared where it is kept.
  const [levelSaving, setLevelSaving] = useState<string | null>(null);
  const [levelError, setLevelError] = useState<unknown>(null);
  useEffect(() => {
    if (!active) return;
    let live = true;
    Promise.all([
      connections.one(defaultNamespace),
      api.capabilities(),
    ]).then(([settings, capabilities]) => {
      const models = capabilities.registries.find(registry => registry.kind === "models");
      if (!models || models.problem) throw new Error(models?.problem || "暂时无法读取可选模型，请刷新状态。");
      if (!live) return;
      setState({
        settings,
        registered: models.entries,
      });
      setError(null);
      setSwitching(null);
      setLevelSaving(null);
      onModels?.(models.entries);
    }).catch(failure => { if (live) { setError(failure); setSwitching(null); setLevelSaving(null); } });
    return () => { live = false; };
    // `onModels` is told, never waited on: a new function each render must not read everything again.
  }, [version, refresh, active, namespace, defaultNamespace]);

  const { current, chosen, standingIn, settled, reason } = modelSettlement(
    state?.settings.values ?? {}, state?.registered ?? [], check);
  useEffect(() => {
    onStatus?.(error ? "unknown" : !state ? "loading" : settled ? "ready" : "missing");
  }, [state, error, settled, onStatus]);
  const names = useMemo(() => modelNames(state?.registered ?? []), [state]);
  // The owner's choice, written straight to the namespace that holds it
  // (ruling 204): not a turn, nothing to follow, and the re-read is the proof.
  const choose = async (key: string) => {
    if (!state || switching || error || !state.registered.some(model => model.key === key)) return;
    setSwitching(key);
    setChoiceError(null);
    try {
      await connections.write(state.settings.namespace, { [defaultField]: key }, state.settings.generation);
      onToast?.(`已设为默认模型「${names.get(key) ?? NAMELESS_MODEL}」。`, "good");
      reload();
    } catch (failure) {
      setChoiceError(failure);
      setSwitching(null);
    }
  };
  // The level is for models somebody else supplies: with none registered there is nothing for it to act on.
  const levelKey = check.suppliedLevelField;
  const levelField = levelKey ? state?.settings.fields.find(field => field.key === levelKey) : undefined;
  const suppliedModels = (state?.registered ?? []).some(model =>
    (model.spec?.metadata as Record<string, unknown> | undefined)?.platform === true);
  const setLevel = async (value: string) => {
    if (!state || !levelKey || !levelField || levelSaving !== null || error) return;
    setLevelSaving(value);
    setLevelError(null);
    try {
      await connections.write(state.settings.namespace, { [levelKey]: value }, state.settings.generation);
      onToast?.(`已改为「${levelField.choice_labels?.[value] ?? value}」，下一句话起就用它。`, "good");
      reload();
    } catch (failure) {
      setLevelError(failure);
      setLevelSaving(null);
    }
  };
  if (onlyWhenNeeded && !error && (!state || settled)) return null;
  const named = (model: CapabilityEntry) => names.get(model.key) ?? NAMELESS_MODEL;
  // The model a turn calls now: the one chosen, or the one standing in for an empty choice.
  const inUse = chosen ?? standingIn;
  // A list is for choosing: with one model in use there is nothing to choose between.
  const listed = editable && state !== null && state.registered.length > 0 && (state.registered.length > 1 || !settled);
  // What runs when the chosen model fails: said in one clause, from what the plugin keeps for it.
  const backups = (fallbackField && Array.isArray(state?.settings.values[fallbackField]) ? state!.settings.values[fallbackField] as unknown[] : [])
    .filter((key): key is string => typeof key === "string" && key !== current && key !== inUse?.key
      && state!.registered.some(model => model.key === key));
  const failover = backups.length
    ? `出问题时依次换用：${backups.map(key => names.get(key) ?? NAMELESS_MODEL).join("、")}。`
    : "没有备用模型，出问题时会直接告诉你。";
  return (
    <section className={`model-readiness${compact ? " model-readiness-compact" : ""}`} aria-label="默认对话模型">
      {!compact ? <Icon name="channels" /> : null}
      <div>
        {listed && inUse && !reason ? null : <strong className={`model-state${state && !settled ? " is-warning" : ""}`}>{error ? "对话模型状态待确认" : !state ? "正在核对默认对话模型"
          : reason ? reason
          : standingIn ? `当前模型：${named(standingIn)}`
          : !state.registered.length ? "未连接对话模型"
          : !current ? "还未选择对话模型" : chosen ? `已设为默认：${named(chosen)}`
          : "已选的对话模型暂不可用"}</strong>}
        {error ? <ErrorState error={error} onRetry={reload} /> : null}
        {editable ? listed && state ? <ModelChoice models={state.registered} names={names} chosen={inUse?.key ?? ""}
          failover={failover} switching={switching} error={choiceError}
          onChoose={key => { void choose(key); }} onRetry={() => setChoiceError(null)} />
          : null : <a href={connectionHref(namespace, sessionId)}>查看模型连接</a>}
        {editable && state && levelKey && levelField && suppliedModels
          ? <ModelLevel field={levelField} value={String(state.settings.values[levelKey] ?? "")} saving={levelSaving}
            error={levelError} onChoose={value => { void setLevel(value); }} onRetry={() => setLevelError(null)} /> : null}
      </div>
    </section>
  );
}
